import React, { useContext, useEffect, useState } from 'react';
import DatahubAPI from '../../../api/DatahubAPI';
import { Redirect, useHistory } from 'react-router-dom';
import format from 'date-fns/format';
import CasesAPI from 'api/CasesAPI';
import { useSnackbar } from 'notistack';
import pako from 'pako';
import { Buffer } from 'buffer';
import { runAIEvaluationSingleProduct } from '../searchComponents/AIEvaluate';
import { includeFields } from 'utils/OSDataHelper';
import { handleShareTicketNotification } from 'utils/NotificationsFuncs';
import { useLocation } from 'react-router-dom';
import { GlobalContext } from 'providers/GlobalProvider';
import { useLogAppAction } from 'hooks/useLogAppAction';
import SearchWorkflow from '../searchComponents/SearchWorkflow';
import CreditsAPI from 'api/CreditsAPI';
import { getWorkflowRunId } from 'utils/workflowRunId';
import { useWorkflowCredits } from 'hooks/useWorkflowCredits';

interface ICommoditiesProps {
  setShowFullRecord: any;
  showFilters: boolean;
  setShowFilters: any;
  cur_user: any;
  userHasCommoditySearch: boolean;
  inputs: any;
  page: number;
  setPage: any;
  commodityState: any;
  isSelected?: boolean;
  isExpands?: boolean;
  resultsOnly?: boolean;
  workflowResults?: any;
  autoOpenFirstLens?: boolean;
  defaultResultLevel?: 2 | 3;
  // Called when the team's one trial run of the Import Workflow has already
  // been spent (see the "Gate AI workflows..." ticket). Lets the top-level
  // chat shell (ChatUI.tsx) swap in TrialRunUsedPanel instead of this
  // component silently showing a misleading "no results" explanation.
  onTrialLimitReached?: () => void;
}

const CommoditiesWorkflow: React.FC<ICommoditiesProps> = ({
  commodityState,
  inputs,
  page,
  setPage,
  //userHasCommoditySearch,
  cur_user,
  setShowFullRecord,
  isSelected,
  isExpands,
  resultsOnly,
  workflowResults,
  autoOpenFirstLens,
  defaultResultLevel,
  onTrialLimitReached,
}) => {
  const location = useLocation();
  const history = useHistory();
  const logAppAction = useLogAppAction();
  const direction = location.pathname.includes('import') ? 'Import' : 'Export';
  const { ticketDraft, setTicketDraft } = useContext(GlobalContext);
  const normalizeWorkflowResults = (results: any) => ({
    isLoading: false,
    data: results ? (Array.isArray(results) ? results : [results]) : [],
  });

  const [commodityReturns, setCommodityReturns] = useState<any>(
    workflowResults !== undefined
      ? normalizeWorkflowResults(workflowResults)
      : {
          isLoading: true,
          data: [],
        },
  );

  const [saving, setSaving] = useState(false);
  const [aiSums, setAiSums] = useState<any>([]);
  const [frequency, setFrequency] = useState('Once');
  const [shareEmail, setShareEmail] = useState<string[]>([]);
  const [ticketNameOpen, setTicketNameOpen] = useState(false);
  const [aiLoading, setAILoading] = useState(false);
  const [newTicketName, setNewTicketName] = useState<string>(
    ticketDraft.ticketName || '',
  );
  const { enqueueSnackbar } = useSnackbar();
  // Re-read before each run starts so isFreeWorkflowTrial knows if it is the free trial run.
  const { refresh: refreshCredits, isFreeWorkflowTrial } = useWorkflowCredits(4, 'import');
  const dt = new Date();
  const dtWithoutTz = new Date(
    dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000,
  );
  const formatted_dt = format(dtWithoutTz, 'yyyy-MM-dd');

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  // `screenedAsTrialRun`: the server ran this step's screening as the team's free trial run (a saved
  // run resumed later is a new browser run, so the hook alone can't tell).
  const spendCredits = async (teamId: string, screenedAsTrialRun = false) => {
    // The team's free trial run: nothing to charge.
    if (screenedAsTrialRun || isFreeWorkflowTrial()) {
      return null;
    }
    const referenceId = ticketDraft._id ? String(ticketDraft._id) : null;
    // The unified spend chooses trial only when it can cover the complete
    // charge, then falls back to paid credits without splitting one run.
    const res = await CreditsAPI.spendCredits({
      teamId: teamId,
      source: 'import_workflow',
      referenceId: referenceId,
      // The screening run is the Import Workflow's last step (Customs Flags).
      // Classification and Tariffs & Duties are billed by Classify/ChatUI.
      step: 3,
      runId: getWorkflowRunId('import_workflow', teamId),
      userId: cur_user?.basics?.id || null,
      userName: [cur_user?.basics?.firstName, cur_user?.basics?.lastName].filter(Boolean).join(' ') || null,
      userEmail: cur_user?.basics?.email || null,
    });
    return res;
  };

  const handleTicket = async (values: any) => {
    if (ticketDraft._id) {
      const new_case = {
        groupId: cur_user.selected_team.id,
        ...values.basics,
        ...ticketDraft,
        ...values,
      };

      const resp = await CasesAPI.updateCase(new_case);
      setTicketDraft({});
      if (resp?.success) {
        shareEmail.forEach((email) => {
          // Call to notification sending function here
          handleShareTicketNotification(
            values.ticketName,
            cur_user,
            email,
            resp.result._id,
          );
        });
        setSaving(false);
        setTicketNameOpen(false);
        setNewTicketName('');
        enqueueSnackbar('Successfully created new case', {
          variant: 'success',
        });
        history.push({
          pathname: `/monitor/ticket/${resp.result._id}`,
        });
      } else if (resp) {
        setSaving(false);
        setTicketNameOpen(false);
        const error_msg =
          'Error saving new case. Uploaded file(s) may be too large.';
        enqueueSnackbar(error_msg, { variant: 'error' });
      }
    } else {
      const new_case = {
        groupId: cur_user.selected_team.id,
        ...values.basics,
        ...values,
      };
      const resp = await CasesAPI.createCase({ newCase: new_case });
      if (resp?.success) {
        shareEmail.forEach((email) => {
          // Call to notification sending function here
          handleShareTicketNotification(
            values.ticketName,
            cur_user,
            email,
            resp.result._id,
          );
        });
        setSaving(false);
        setTicketNameOpen(false);
        setNewTicketName('');
        enqueueSnackbar('Successfully created new case', {
          variant: 'success',
        });
        history.push({
          pathname: `/monitor/ticket/${resp.result._id}`,
        });
      } else if (resp) {
        setSaving(false);
        setTicketNameOpen(false);
        const error_msg =
          'Error saving new case. Uploaded file(s) may be too large.';
        enqueueSnackbar(error_msg, { variant: 'error' });
      }
    }
  };

  const compressData = (data: any) => {
    const stringifiedData = JSON.stringify(data);
    const compressed = pako.gzip(stringifiedData);
    return Buffer.from(compressed).toString('base64'); // Convert to base64 for storage or transmission
  };

  const createTicket = async (name: string) => {
    setSaving(true);
    const comFilterQuery = {
      lenses: commodityState.comLenses,
      countries: commodityState.countries,
      agencies: commodityState.agencies,
      lists: commodityState.lists,
      hsCodeLists: commodityState.hsCodeLists,
      sections: commodityState.sections,
      chapters: commodityState.chapters,
      codes: commodityState.codes,
      entRes: commodityState.fuzzy,
      filterDate: commodityState.dateRange,
    };

    const entFilterQuery = {
      lenses: [],
      countries: [],
      agencies: [],
      lists: [],
      entRes: false,
      filterDate: [],
    };

    const ownerQuery = {
      kc_id: cur_user.basics?.id,
      email: cur_user.basics?.email,
      firstName: cur_user.basics?.firstName,
      lastName: cur_user.basics?.lastName,
    };

    const query: any = {
      ticketName: ticketDraft.ticketName ? ticketDraft.ticketName : name,
      lastUpdate: formatted_dt,
      activityType: 'workflow',
      workflowType: 'import',
      commodity: {
        harmonizedScheduleCode: inputs.hsCode,
        countryOfOrigin: inputs.origin,
        destinationCountry: inputs.destination || 'USA',
        material: ticketDraft.commodity?.material
          ? ticketDraft.commodity?.material
          : '',
        intendedUse: ticketDraft.commodity?.intendedUse
          ? ticketDraft.commodity?.intendedUse
          : '',
        physicalState: ticketDraft.commodity?.physicalState
          ? ticketDraft.commodity?.physicalState
          : '',
      },
      docType: 'Import Unified',
      commodityFilter: comFilterQuery,
      entityFilter: entFilterQuery,
      frequency: frequency,
      sharedWith: shareEmail,
      aiSums: aiSums,
      activeFilter: '',
      selectedOpts: [],
      status: 'Complete',
      favorite: false,
      groupName: cur_user.selected_team.name,
      template: [],
      colMapping: [],
      activity: {
        user: {
          kc_id: cur_user.basics?.id,
          email: cur_user.basics?.email,
          firstName: cur_user.basics?.firstName,
          lastName: cur_user.basics?.lastName,
        },
        action: 'created monitor ticket',
        timeStamp: Date.now(),
      },
      owner: ownerQuery,
    };
    const extractSources = (docs: any): any[] => {
      return docs.map((doc: any) => doc._source);
    };
    const length = commodityReturns.data[0].result.body.hits.total.value;
    const structuredObj = [
      {
        dataSet: 'Commodities',
        docs: extractSources(commodityReturns.data[0].result.body.hits.hits),
        field: inputs.hsCode.length > 0 ? inputs.hsCode : inputs.desc,
        hits: length,
        row: 1,
      },
    ];

    const ticketQuery = {
      ...query,
      curTotal: length,
      curNew: 0,
      results: {
        allResults: compressData(structuredObj),
        allTotal: length,
        newResults: [],
        newTotal: 0,
        date: formatted_dt,
        docInfo: [],
      },
    };
    handleTicket(ticketQuery);
  };

  const createDraftTicket = async (name: string) => {
    setSaving(true);
    const comFilterQuery = {
      lenses: commodityState.comLenses,
      countries: commodityState.countries,
      agencies: commodityState.agencies,
      lists: commodityState.lists,
      hsCodeLists: commodityState.hsCodeLists,
      sections: commodityState.sections,
      chapters: commodityState.chapters,
      codes: commodityState.codes,
      entRes: commodityState.fuzzy,
      filterDate: commodityState.dateRange,
    };

    const entFilterQuery = {
      lenses: [],
      countries: [],
      agencies: [],
      lists: [],
      entRes: false,
      filterDate: [],
    };

    const ownerQuery = {
      kc_id: cur_user.basics?.id,
      email: cur_user.basics?.email,
      firstName: cur_user.basics?.firstName,
      lastName: cur_user.basics?.lastName,
    };

    const query: any = {
      ticketName: ticketDraft.ticketName ? ticketDraft.ticketName : name,
      lastUpdate: formatted_dt,
      activityType: 'workflow',
      workflowType: 'import',
      commodity: {
        harmonizedScheduleCode: inputs.hsCode,
        commodityName: ticketDraft.commodity?.commodityName
          ? ticketDraft.commodity?.commodityName
          : '',
        countryOfOrigin: inputs.origin,
        destinationCountry: inputs.destination || 'USA',
        material: ticketDraft.commodity?.material
          ? ticketDraft.commodity?.material
          : '',
        intendedUse: ticketDraft.commodity?.intendedUse
          ? ticketDraft.commodity?.intendedUse
          : '',
        physicalState: ticketDraft.commodity?.physicalState
          ? ticketDraft.commodity?.physicalState
          : '',
      },
      docType: 'Import Unified',
      commodityFilter: comFilterQuery,
      entityFilter: entFilterQuery,
      frequency: frequency,
      sharedWith: shareEmail,
      aiSums: aiSums,
      activeFilter: '',
      selectedOpts: [],
      status: 'Complete',
      favorite: false,
      groupName: cur_user.selected_team.name,
      template: [],
      colMapping: [],
      activity: {
        user: {
          kc_id: cur_user.basics?.id,
          email: cur_user.basics?.email,
          firstName: cur_user.basics?.firstName,
          lastName: cur_user.basics?.lastName,
        },
        action: 'created monitor ticket',
        timeStamp: Date.now(),
      },
      owner: ownerQuery,
    };
    const extractSources = (docs: any): any[] => {
      return docs.map((doc: any) => doc._source);
    };
    const length = commodityReturns.data[0].result.body.hits.total.value;
    const structuredObj = [
      {
        dataSet: 'Commodities',
        docs: extractSources(commodityReturns.data[0].result.body.hits.hits),
        field: inputs.hsCode.length > 0 ? inputs.hsCode : inputs.desc,
        hits: length,
        row: 1,
      },
    ];

    const ticketQuery = {
      ...query,
      curTotal: length,
      curNew: 0,
      results: {
        allResults: compressData(structuredObj),
        allTotal: length,
        newResults: [],
        newTotal: 0,
        date: formatted_dt,
        docInfo: [],
      },
    };
    const new_case = {
      groupId: cur_user.selected_team.id,
      ...ticketQuery.basics,
      ...ticketDraft,
      ...ticketQuery,
    };

    const draftUpdate = {
      ...ticketDraft,
      ...new_case,
    };
    if (!draftUpdate.ticketName) {
      delete draftUpdate.ticketName;
    }
    setTicketDraft(draftUpdate);
  };

  const getComCSVResults = async (pageNum: number) => {
    const allResults: any = [];

    let val = '';
    const formatCode = (str: string, n: number) => {
      const ret: string[] = [];
      let i = 0;
      let len = str.length;

      for (i = 0, len = str.length; i < len; i = i + n) {
        if (i === 0) {
          ret.push(str.substr(i, n + 2));
        } else if (i > 2) {
          ret.push(str.substr(i, n));
        }
      }
      return ret;
    };
    const re = new RegExp('^[0-9]+$');
    if (re.test(inputs.hsCode)) {
      val = formatCode(inputs.hsCode, 2).join('.');
    }
    const query = {
      primary: val.length > 0 ? val : inputs.desc,
      indices: ['bite_app_commodities_*'],
      lens:
        commodityState.countries.length > 0 ||
        commodityState.agencies.length > 0 ||
        commodityState.lists.length > 0
          ? []
          : commodityState.lenses,
      listCountry:
        commodityState.agencies.length > 0 || commodityState.lists.length > 0
          ? []
          : commodityState.countries,
      agency: commodityState.lists.length > 0 ? [] : commodityState.agencies,
      listName: [...commodityState.lists, ...commodityState.hsCodeLists],
      hsCodeSection: commodityState.sections,
      hsCodeChapter: commodityState.chapters,
      hsCode: commodityState.codes,
      dateRange: commodityState.dateRange,
      agg: false,
      pageNum: pageNum,
      fuzziness: commodityState.fuzzy ? 'AUTO' : 0,
      qty: 200,
    };
    const res = await DatahubAPI.commoditySearch(query);
    if (res?.success) {
      allResults.push(res?.result);
    }

    return allResults;
  };

  const formatHTS = (input: string): string => {
    if (input.length === 0) {
      return '';
    }
    // Remove all non-digit characters
    const digitsOnly = input.replace(/\D/g, '');

    // Split into groups: 4, 2, 2, 2
    const parts = [];
    if (
      digitsOnly.length !== 6 &&
      digitsOnly.length !== 8 &&
      digitsOnly.length !== 10
    ) {
      const error_msg =
        'Please enter a valid HTS Code that is 6, 8 or 10 digits.';
      enqueueSnackbar(error_msg, { variant: 'error' });
      return 'error';
    }

    if (digitsOnly.length >= 4) {
      parts.push(digitsOnly.slice(0, 4));
    }
    if (digitsOnly.length >= 6) {
      parts.push(digitsOnly.slice(4, 6));
    }
    if (digitsOnly.length >= 8) {
      parts.push(digitsOnly.slice(6, 8));
    }
    if (digitsOnly.length === 10) {
      parts.push(digitsOnly.slice(8, 10));
    }

    return parts.join('.');
  };

  const handleSubmit = async (pageNum: number) => {
    if (
      inputs.hsCode.length === 0 &&
      inputs.desc.length === 0 &&
      commodityState.lists.length === 0 &&
      commodityState.hsCodeLists.length === 0
    ) {
      enqueueSnackbar(
        'Please enter an HTS Code or product description to screen.',
        {
          variant: 'error',
        },
      );
      return;
    }

    const val = formatHTS(inputs.hsCode);
    if (val === 'error') {
      return;
    }
    logAppAction('import_workflow:started');
    setAILoading(true);
    await refreshCredits();
    setCommodityReturns({ isLoading: true, data: [] });
    setAiSums([]);
    const allResults: any = [];
    const aiSummaries: any = [];
    let screenedAsTrialRun = false;

    const query = {
      primary: val.length > 0 ? val : inputs.desc,
      indices: ['bite_app_commodities_*'],
      lens: commodityState.comLenses,
      listCountry:
        commodityState.agencies.length > 0 || commodityState.lists.length > 0
          ? []
          : commodityState.countries,
      agency: commodityState.lists.length > 0 ? [] : commodityState.agencies,
      listName: [...commodityState.lists, ...commodityState.hsCodeLists],
      hsCodeSection:
        commodityState.codes.length > 0 || commodityState.chapters > 0
          ? []
          : commodityState.sections,
      hsCodeChapter:
        commodityState.codes.length > 0 ? [] : commodityState.chapters,
      hsCode: commodityState.codes,
      dateRange: commodityState.dateRange,
      agg: true,
      type: direction,
      pageNum: pageNum,
    };
    const res = await DatahubAPI.commoditySearch(query);
    if (res?.success) {
      allResults.push(res?.result);
      setCommodityReturns({ isLoading: false, data: allResults });

      if (inputs.hsCode.length > 0) {
        const fields = [
          'HS Code',
          'BITE Lens',
          'BITE-Identified Risk Country',
          'List Name',
          'Trade Type',
        ];
        const hits = res?.result?.result?.body?.hits?.hits;

        if (inputs.origin?.length < 1 || inputs.destination?.length < 1) {
          const ai_summary = {
            hs_code: '',
            explanation:
              'Please fill out all fields labeled as Required for AI in order to get an AI Evaluation on your screen',
            trade_category: '',
            burden: '',
            key_factors: [],
            risk_level: '',
          };
          aiSummaries.push(ai_summary);
        } else if (hits.length > 1000) {
          const ai_summary = {
            hs_code: inputs.hsCode,
            explanation:
              'Your searched returned over 1,000 results. Please narrow down your search by adjusting your filters or providing a more specific value if you would like an Evaluation.',
            trade_category: '',
            burden: '',
            key_factors: [],
            risk_level: '',
          };
          aiSummaries.push(ai_summary);
        } else {
          // Step 1: Extract _source from hits
          const hit_list = hits.map((hit: any) => hit._source);

          // Step 2: Filter down to only the specified fields
          const filtered = await includeFields(fields, hit_list);

          // Step 3: Deduplicate by 'List Name' while preserving the filtered fields
          const seenListNames = new Set();
          const uniqueFiltered = filtered.filter((item: any) => {
            const listName = item['List Name'];
            if (!listName || seenListNames.has(listName)) {
              return false;
            }
            seenListNames.add(listName);
            return true;
          });

          // Step 4: Run the AI evaluation on the unique filtered list
          const ai_summary = await runAIEvaluationSingleProduct(
            inputs.hsCode,
            inputs.origin,
            inputs.destination,
            JSON.stringify(uniqueFiltered),
            direction,
            cur_user.selected_team?.id,
            undefined,
            // The run's HTS classification reserved the trial for this run id; the screening carries it on.
            getWorkflowRunId('import_workflow', cur_user.selected_team?.id || ''),
          );
          screenedAsTrialRun = Boolean(ai_summary?.trial_run);

          if (ai_summary?.hs_code === 'Trial run used') {
            onTrialLimitReached?.();
            setAILoading(false);
            return;
          }

          aiSummaries.push(ai_summary);
        }
      } else {
        const ai_summary = {
          hs_code: '',
          explanation:
            'Please fill out all fields labeled as Required for AI in order to get an AI Evaluation on your screen',
          burden: '',
          key_factors: [],
          trade_category: '',
          risk_level: '',
          screenDate: new Date().toLocaleDateString(),
        };
        aiSummaries.push(ai_summary);
      }
    }
    setAiSums(aiSummaries);
    setAILoading(false);
    try {
      logAppAction('import_workflow:completed');
    } catch (error) {
      console.error('[BO] Failed to log investigation completion', error);
    }
    spendCredits(cur_user.selected_team.id, screenedAsTrialRun);
  };

  const handleMultiPageChange = async (
    input: any,
    pageNum: number,
    i: number,
  ) => {
    const allResults: any = commodityReturns.data;
    const query = {
      primary: input,
      indices: ['bite_app_commodities_*'],
      lens: commodityState.comLenses,
      listCountry:
        commodityState.agencies.length > 0 || commodityState.lists.length > 0
          ? []
          : commodityState.countries,
      agency: commodityState.lists.length > 0 ? [] : commodityState.agencies,
      listName: [...commodityState.lists, ...commodityState.hsCodeLists],
      hsCodeSection:
        commodityState.codes.length > 0 || commodityState.chapters > 0
          ? []
          : commodityState.sections,
      hsCodeChapter:
        commodityState.codes.length > 0 ? [] : commodityState.chapters,
      hsCode: commodityState.codes,
      dateRange: commodityState.dateRange,
      agg: true,
      type: direction,
      pageNum: pageNum,
    };
    const res = await DatahubAPI.commoditySearch(query);
    if (res?.success) {
      allResults[i] = res?.result;
    }
    setCommodityReturns({ isLoading: false, data: allResults });
  };

  useEffect(() => {
    setPage(0);
  }, [inputs]);

  useEffect(() => {
    if (workflowResults !== undefined) {
      setCommodityReturns(normalizeWorkflowResults(workflowResults));
    }
  }, [workflowResults]);

  useEffect(() => {
    if (aiSums.length > 0 && commodityReturns.data.length) {
      createDraftTicket('');
    }
  }, [aiSums, commodityReturns.data]);

  useEffect(() => {
    if (
      workflowResults === undefined &&
      inputs.hsCode &&
      inputs.origin &&
      inputs.destination
    ) {
      handleSubmit(page);
    }
  }, [inputs.hsCode, inputs.origin, inputs.destination, workflowResults]);

  const handleOpenModal = async () => {
    setTicketNameOpen(true);
  };

  return (
    <div className='screening-body-container'>
      <SearchWorkflow
        setShowFullRecord={setShowFullRecord}
        type={'Products'}
        handleSubmit={handleSubmit}
        inputs={inputs}
        results={commodityReturns}
        page={page}
        setPage={setPage}
        cyberHeaders={[]}
        getCSVResults={getComCSVResults}
        handleMultiPageChange={handleMultiPageChange}
        createTicket={createTicket}
        saving={saving}
        aiSums={aiSums}
        shareEmail={shareEmail}
        setShareEmail={setShareEmail}
        setFrequency={setFrequency}
        frequency={frequency}
        ticketNameOpen={ticketNameOpen}
        setTicketNameOpen={setTicketNameOpen}
        aiLoading={aiLoading}
        newTicketName={newTicketName}
        setNewTicketName={setNewTicketName}
        handleOpenModal={handleOpenModal}
        isSelected={isSelected}
        isExpands={isExpands}
        resultsOnly={resultsOnly}
        autoOpenFirstLens={autoOpenFirstLens}
        defaultResultLevel={defaultResultLevel}
      />
    </div>
  );
};

export default CommoditiesWorkflow;
